import { useState, useRef } from 'react'

/**
 * PlanButton component that allows users to add events/places to their traveling plans
 * Now supports multiple colored plans with a popup selection interface
 */
export default function PlanButton({
    item,
    itemPlanColors = [], // Array of color strings for plans this item belongs to
    onShowPopup, // Callback to show the plan selection popup
    variant = 'default'
}) {
    const buttonRef = useRef(null)
    const isInAnyPlan = itemPlanColors.length > 0

    const handleClick = e => {
        e.preventDefault()
        e.stopPropagation()
        onShowPopup(item, buttonRef.current)
    }

    const baseClasses =
        'relative flex items-center gap-1 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'

    const variantClasses = {
        default: `px-3 py-2 text-sm font-medium ${
            isInAnyPlan
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
        }`,
        icon: `p-2 ${
            isInAnyPlan ? 'text-blue-500 hover:text-blue-600' : 'text-gray-400 hover:text-gray-600'
        }`,
        compact: `px-2 py-1 text-xs ${
            isInAnyPlan
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
        }`,
        'hover-circle': `p-2.5 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 shadow-lg ${
            isInAnyPlan ? 'text-blue-500' : 'text-white'
        }`,
    }

    return (
        <button
            ref={buttonRef}
            onClick={handleClick}
            className={`${baseClasses} ${variantClasses[variant]}`}
            aria-label={isInAnyPlan ? 'Manage plans' : 'Add to plan'}
            title={isInAnyPlan ? 'Manage plans' : 'Add to plan'}
        >
            {/* Color indicators for plans this item belongs to */}
            {isInAnyPlan && itemPlanColors.length > 0 && variant !== 'icon' && (
                <div className="flex gap-0.5 absolute -top-1 -right-1">
                    {itemPlanColors.slice(0, 3).map((color, index) => (
                        <div
                            key={index}
                            className="w-2.5 h-2.5 rounded-full border border-white shadow-sm"
                            style={{ backgroundColor: color }}
                        />
                    ))}
                    {itemPlanColors.length > 3 && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gray-400 border border-white shadow-sm flex items-center justify-center">
                            <span className="text-[6px] text-white font-bold">+</span>
                        </div>
                    )}
                </div>
            )}

            {isInAnyPlan ? (
                // Checkmark icon when in any plan
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
            ) : (
                // Plus icon when not in any plan
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
            )}
            {variant === 'default' && (
                <span>
                    {isInAnyPlan
                        ? itemPlanColors.length === 1
                            ? 'In Plan'
                            : `In ${itemPlanColors.length} Plans`
                        : 'Add to Plan'}
                </span>
            )}
        </button>
    )
}
